import type { OutputChunk } from "../nest/generated"

/**
 * Reassembles runtime output chunks into a strict, gap-tolerant, exactly-once
 * stream keyed on the server-assigned chunk offset.
 *
 * Terminal and process output is delivered from two concurrent sources — the
 * live `*.output` subscription and the `*.read` replay — that can interleave and
 * arrive out of offset order. A live chunk that lands mid-replay must not drop
 * the replay's lower-offset chunks: an interactive shell constantly redraws its
 * input line with `\r`/cursor escapes, so a single dropped chunk turns a normal
 * redraw into visibly duplicated text (e.g. `git status` renders as `gitOn
 * branch master`). Chunks are buffered by offset and drained contiguously so the
 * consumer sees every byte exactly once, in order, regardless of arrival order.
 */
export class OutputChunkOrderer {
    private nextOffset = 0
    private pending = new Map<number, OutputChunk>()

    /** The offset a replay read should resume from. */
    get readOffset(): number {
        return this.nextOffset
    }

    /** Feed one chunk from either source; delivers any now-contiguous chunks in order. */
    accept(chunk: OutputChunk, deliver: (chunk: OutputChunk) => void): void {
        if (chunk.offset < this.nextOffset) return // already delivered
        this.pending.set(chunk.offset, chunk)
        let next = this.pending.get(this.nextOffset)
        while (next) {
            this.pending.delete(this.nextOffset)
            this.nextOffset += 1
            deliver(next)
            next = this.pending.get(this.nextOffset)
        }
    }

    /**
     * Advance past chunks the server has already trimmed from its replay buffer so
     * a permanent low-offset gap cannot stall delivery of newer output.
     */
    skipTrimmed(firstAvailableOffset: number): void {
        if (firstAvailableOffset <= this.nextOffset) return
        this.nextOffset = firstAvailableOffset
        for (const offset of this.pending.keys()) {
            if (offset < firstAvailableOffset) this.pending.delete(offset)
        }
    }
}
